const CELEBRATED_RANKS_KEY = "wizmate.celebratedRanks.v1";

export type CelebrationSurface = "chat" | "home";

type CelebrationRecord = {
  rankName: string;
  confirmedOn: Partial<Record<CelebrationSurface, true>>;
};

function normalizeConfirmedOn(value: unknown): Partial<Record<CelebrationSurface, true>> {
  const confirmedOn: Partial<Record<CelebrationSurface, true>> = {};

  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return confirmedOn;
  }

  if ((value as Record<string, unknown>).chat === true) {
    confirmedOn.chat = true;
  }
  if ((value as Record<string, unknown>).home === true) {
    confirmedOn.home = true;
  }

  return confirmedOn;
}

function normalizeCelebrationRecord(value: unknown): CelebrationRecord | null {
  if (typeof value === "string") {
    // Legacy format: { [characterId]: "rankName" }
    return { rankName: value, confirmedOn: {} };
  }

  if (!value || typeof value !== "object" || Array.isArray(value)) return null;

  const rawRankName = (value as { rankName?: unknown }).rankName;
  if (typeof rawRankName !== "string" || !rawRankName) return null;

  // `confirmedOn` is the current schema. Older `shownOn` entries came from the
  // auto-close implementation, so we intentionally ignore them to avoid suppressing
  // the toast after switching to explicit confirmation.
  const confirmedOn = normalizeConfirmedOn((value as { confirmedOn?: unknown }).confirmedOn);

  return {
    rankName: rawRankName,
    confirmedOn,
  };
}

function readCelebratedRanks(): Record<string, CelebrationRecord> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(CELEBRATED_RANKS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};

    const result: Record<string, CelebrationRecord> = {};
    for (const [k, v] of Object.entries(parsed)) {
      const normalized = normalizeCelebrationRecord(v);
      if (typeof k === "string" && normalized) {
        result[k] = normalized;
      }
    }
    return result;
  } catch {
    return {};
  }
}

export function isCelebrated(
  characterId: string,
  rankName: string,
  surface?: CelebrationSurface
): boolean {
  const record = readCelebratedRanks()[characterId];
  if (!record || record.rankName !== rankName) return false;
  if (!surface) return true;
  return record.confirmedOn[surface] === true;
}

export function getPendingCelebrationRank(
  characterId: string,
  surface: CelebrationSurface
): string | null {
  const record = readCelebratedRanks()[characterId];
  if (!record) return null;
  const confirmedOnSomeSurface = record.confirmedOn.chat === true || record.confirmedOn.home === true;
  if (confirmedOnSomeSurface) return null;
  if (record.confirmedOn[surface] === true) return null;
  return record.rankName || null;
}

export function markRankAsDetected(characterId: string, rankName: string): void {
  markRankAsDetectedWithOptions(characterId, rankName);
}

export function markRankAsDetectedWithOptions(
  characterId: string,
  rankName: string,
  options?: {
    preserveConfirmedOnWhenSameRank?: boolean;
  }
): void {
  if (typeof window === "undefined") return;

  const records = readCelebratedRanks();
  const current = records[characterId];
  const preserveConfirmedOnWhenSameRank = options?.preserveConfirmedOnWhenSameRank ?? true;
  const shouldPreserveConfirmedOn =
    preserveConfirmedOnWhenSameRank && current?.rankName === rankName;

  records[characterId] = {
    rankName,
    confirmedOn: shouldPreserveConfirmedOn ? { ...(current.confirmedOn ?? {}) } : {},
  };

  window.localStorage.setItem(CELEBRATED_RANKS_KEY, JSON.stringify(records));
}

export function markAsCelebrated(
  characterId: string,
  rankName: string,
  surface?: CelebrationSurface
): void {
  if (typeof window === "undefined") return;

  const records = readCelebratedRanks();
  const current = records[characterId];
  const confirmedOn =
    current?.rankName === rankName
      ? { ...(current.confirmedOn ?? {}) }
      : ({} as Partial<Record<CelebrationSurface, true>>);

  if (surface) {
    confirmedOn[surface] = true;
  }

  records[characterId] = {
    rankName,
    confirmedOn,
  };

  window.localStorage.setItem(CELEBRATED_RANKS_KEY, JSON.stringify(records));
}
